/*
 * Session state.
 *
 * There is no token in here on purpose. The browser holds the session in
 * httpOnly cookies it sends automatically, so "am I signed in?" is answered by
 * asking the server (GET /partner/auth/me), not by reading local state. A
 * reload therefore always reflects reality rather than a stale flag in
 * localStorage.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError, SESSION_EXPIRED, auth, type PartnerMe, type SessionExpiredDetail } from "../api/client";

/** "unreachable": the server could not be asked (network or 5xx), which is
 * not the same as being signed out. "no_partner_api": a server answered, but
 * 404 on /partner/auth/me — it is a Backend-v2 without the partner routes
 * (in dev, usually VITE_API_TARGET pointing at the wrong API). */
type Status = "checking" | "signed_in" | "signed_out" | "unreachable" | "no_partner_api";

type AuthState = {
  status: Status;
  me: PartnerMe | null;
  /** Why the last session ended when the server said so, e.g.
   * "This partner account is suspended." Shown on the sign-in screen. */
  endedMessage: string | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  onSignedIn: () => Promise<void>;
  /** Replace the signed-in partner with a fresher copy (after PATCH /profile). */
  setMe: (me: PartnerMe) => void;
};

const Ctx = createContext<AuthState | null>(null);

/* The message outlives a reload of this tab: the call that reported it is not
 * going to be repeated, and asking the server again only says "no session". */
const ENDED_KEY = "wop:session-ended";

function storedMessage(): string | null {
  try {
    return sessionStorage.getItem(ENDED_KEY);
  } catch {
    return null;
  }
}

function storeMessage(message: string | null) {
  try {
    if (message) sessionStorage.setItem(ENDED_KEY, message);
    else sessionStorage.removeItem(ENDED_KEY);
  } catch {
    /* storage blocked: the notice just does not survive a reload */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("checking");
  const [me, setMeState] = useState<PartnerMe | null>(null);
  const [endedMessage, setEndedState] = useState<string | null>(storedMessage);
  const setEnded = useCallback((message: string | null) => {
    storeMessage(message);
    setEndedState(message);
  }, []);

  const signedOut = useCallback(() => {
    setMeState(null);
    setStatus("signed_out");
  }, []);

  // One load at a time: StrictMode and a sign-in landing mid-load would
  // otherwise race two session checks against each other.
  const inFlight = useRef<Promise<void> | null>(null);

  const load = useCallback(() => {
    // api() already rotates an expired access cookie once before giving up,
    // so a 401 here means the session is really over.
    inFlight.current ??= auth
      .me()
      .then((next) => {
        setEnded(null);
        setMeState(next);
        setStatus("signed_in");
      })
      .catch((err) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          if (err.status === 403 && /partner account is (suspended|closed)/i.test(err.message)) setEnded(err.message);
          signedOut();
        } else if (err instanceof ApiError && err.status === 404) {
          setStatus("no_partner_api");
        } else {
          setStatus("unreachable");
        }
      })
      .finally(() => {
        inFlight.current = null;
      });
    return inFlight.current;
  }, [signedOut, setEnded]);

  useEffect(() => {
    void load();
  }, [load]);

  // A call anywhere in the app found the session gone and could not renew it,
  // or found the partner account suspended or closed.
  useEffect(() => {
    const onExpired = (e: Event) => {
      const message = (e as CustomEvent<SessionExpiredDetail>).detail?.message ?? null;
      if (message) setEnded(message);
      signedOut();
    };
    window.addEventListener(SESSION_EXPIRED, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED, onExpired);
  }, [signedOut, setEnded]);

  const onSignedIn = useCallback(async () => {
    setEnded(null);
    await load();
  }, [load, setEnded]);

  const signOut = useCallback(async () => {
    setEnded(null);
    try {
      await auth.logout();
    } catch {
      // Signed out locally either way; the refresh cookie expires on its own.
    } finally {
      signedOut();
    }
  }, [signedOut, setEnded]);

  const setMe = useCallback((next: PartnerMe) => setMeState(next), []);

  const value = useMemo<AuthState>(
    () => ({ status, me, endedMessage, refresh: load, signOut, onSignedIn, setMe }),
    [status, me, endedMessage, load, signOut, onSignedIn, setMe],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** For pages inside the signed-in shell, where `me` is always set. */
export function useMe(): PartnerMe {
  const { me } = useAuth();
  if (!me) throw new Error("useMe is only for the signed-in shell");
  return me;
}
